// ValueEffortScoring - Value/Effort Matrix scoring interface
// Implements US-3.3: Matrix Scoring Frameworks (Value/Effort, Eisenhower, Risk Assessment)

import React, { useState, useEffect, useCallback } from 'react';
import styles from './MatrixScoring.module.css';
import { Item, Score, CalculatedResult } from '../types/domain';
import { updateScores } from '../services/scoreService';

interface ValueEffortScoringProps {
  item: Item;
  isReadOnly: boolean;
  onScoresUpdated?: (itemId: string, scores: Score[], result: CalculatedResult) => void;
}

/**
 * Value/Effort Matrix scoring component
 * - Displays 2 input fields: Value (1-5), Effort (1-5)
 * - Calculates and displays quadrant: Quick Wins, Major Projects, Fill-Ins, Time Wasters
 * - Auto-saves on value change with debouncing
 */
export const ValueEffortScoring: React.FC<ValueEffortScoringProps> = ({
  item,
  isReadOnly,
  onScoresUpdated,
}) => {
  // EXTRACT current scores from item
  const getScoreValue = (criterion: string): number => {
    const score = item.scores?.find((s) => s.criterion === criterion);
    return score ? Number(score.value) : 0;
  };

  // STATE for score inputs
  const [value, setValue] = useState<number>(getScoreValue('value'));
  const [effort, setEffort] = useState<number>(getScoreValue('effort'));
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // UPDATE local state when item scores change (real-time updates from other users)
  useEffect(() => {
    setValue(getScoreValue('value'));
    setEffort(getScoreValue('effort'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.scores]);

  /**
   * CALCULATE quadrant locally for immediate feedback
   * Quadrants: Quick Wins, Major Projects, Fill-Ins, Time Wasters
   * NOTE: No useCallback needed - function is only called inline, not passed as prop
   */
  const calculateQuadrant = (v: number, e: number): string | null => {
    if (v === 0 || e === 0) return null;

    if (v >= 3 && e <= 2) return 'Quick Wins';
    if (v >= 3 && e >= 3) return 'Major Projects';
    if (v <= 2 && e <= 2) return 'Fill-Ins';
    if (v <= 2 && e >= 3) return 'Time Wasters';
    return null;
  };

  const calculatedQuadrant = calculateQuadrant(value, effort);

  /**
   * SAVE scores to backend
   * NOTE: Debounced to avoid excessive API calls
   */
  const saveScores = useCallback(
    async (v: number, e: number) => {
      // VALIDATE: Only save if both values are set
      if (v === 0 || e === 0) {
        return;
      }

      try {
        setIsSaving(true);
        setError(null);

        const response = await updateScores(item.id, [
          { criterion: 'value', value: v },
          { criterion: 'effort', value: e },
        ]);

        if (!response.success) {
          throw new Error(response.error || 'Failed to save scores');
        }

        // NOTIFY parent component of score update
        if (onScoresUpdated && response.scores && response.calculatedResult) {
          onScoresUpdated(item.id, response.scores, response.calculatedResult);
        }
      } catch (err) {
        console.error('Failed to save scores:', err);
        setError(err instanceof Error ? err.message : 'Failed to save scores');
      } finally {
        setIsSaving(false);
      }
    },
    [item.id, onScoresUpdated]
  );

  /**
   * DEBOUNCE score saving (wait 1 second after last change)
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isReadOnly && value > 0 && effort > 0) {
        saveScores(value, effort);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [value, effort, isReadOnly, saveScores]);

  /**
   * HANDLE input change with validation
   */
  const handleInputChange = (criterion: 'value' | 'effort', inputValue: string) => {
    const numValue = parseInt(inputValue, 10);

    // VALIDATE: Empty input
    if (inputValue === '') {
      criterion === 'value' ? setValue(0) : setEffort(0);
      return;
    }

    // VALIDATE: Must be number between 1-5
    if (isNaN(numValue) || numValue < 1 || numValue > 5) {
      return;
    }

    // UPDATE state
    criterion === 'value' ? setValue(numValue) : setEffort(numValue);
  };

  /**
   * GET quadrant color class for visual distinction
   */
  const getQuadrantClass = (quadrant: string | null): string => {
    if (!quadrant) return '';
    switch (quadrant) {
      case 'Quick Wins':
        return styles.quadrantQuickWins || '';
      case 'Major Projects':
        return styles.quadrantMajor || '';
      case 'Fill-Ins':
        return styles.quadrantFillIns || '';
      case 'Time Wasters':
        return styles.quadrantWasters || '';
      default:
        return '';
    }
  };

  return (
    <div className={styles.matrixScoring}>
      <div className={styles.criteriaGrid}>
        {/* VALUE input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`value-${item.id}`}>Value</label>
          <input
            id={`value-${item.id}`}
            type="number"
            min="1"
            max="5"
            value={value || ''}
            onChange={(e) => handleInputChange('value', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-5"
            className={styles.input}
          />
        </div>

        {/* EFFORT input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`effort-${item.id}`}>Effort</label>
          <input
            id={`effort-${item.id}`}
            type="number"
            min="1"
            max="5"
            value={effort || ''}
            onChange={(e) => handleInputChange('effort', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-5"
            className={styles.input}
          />
        </div>
      </div>

      {/* CALCULATED QUADRANT display */}
      {calculatedQuadrant && (
        <div className={`${styles.resultDisplay} ${getQuadrantClass(calculatedQuadrant)}`}>
          <span className={styles.resultLabel}>Quadrant:</span>
          <span className={styles.resultValue}>{calculatedQuadrant}</span>
          {isSaving && <span className={styles.savingIndicator}>Speichere...</span>}
        </div>
      )}

      {/* ERROR message */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* UNSCORED indicator */}
      {!calculatedQuadrant && !isReadOnly && (
        <div className={styles.unscoredMessage}>Gib beide Kriterien ein, um den Quadrant zu berechnen</div>
      )}
    </div>
  );
};
